import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Snowflake, X, ArrowRight } from 'lucide-react';
import { tyresAPI } from '../services/api';
import { useSelectedVehicle } from '../services/vehicleStore';
import { useT } from '../services/i18n';

const KEY = 'winterBannerDismissedAt';
const WEEK = 7 * 24 * 3600 * 1000;
const SHOW_ON = /^\/($|pieces|produit|recherche|categorie)/;
const dismissed = () => { const t = parseInt(localStorage.getItem(KEY) || '0', 10); return t && Date.now() - t < WEEK; };

export const useWinterBanner = () => {
  const { t } = useT();
  const { pathname } = useLocation();
  const vehicle = useSelectedVehicle();
  const [banner, setBanner] = useState(null);
  const [hidden, setHidden] = useState(dismissed);

  useEffect(() => {
    if (hidden || !SHOW_ON.test(pathname)) return;
    const p = vehicle ? { brand: vehicle.brand, model: vehicle.model, year: vehicle.year || undefined, engine: vehicle.engine?.name || undefined } : {};
    tyresAPI.winterBanner(p).then((b) => setBanner(b?.active ? b : null)).catch(() => setBanner(null));
  }, [vehicle, pathname, hidden]);

  if (hidden || !banner || !SHOW_ON.test(pathname) || pathname.startsWith('/pieces/pneus-jantes-et-accessoires')) return null;
  const close = () => { localStorage.setItem(KEY, String(Date.now())); setHidden(true); };
  const cta = banner.has_fitment ? `${t('winter.myTyres')}${banner.winter_count ? ` (${banner.winter_count})` : ''}` : t('winter.seeTyres');
  return { banner, vehicle, cta, close };
};

const WinterTyresBanner = ({ className = '' }) => {
  const { t, lang } = useT();
  const w = useWinterBanner();
  if (!w) return null;
  const { banner, vehicle, cta, close } = w;

  return (
    <div className={`bg-sky-950 text-sky-50 text-sm border-b border-sky-800 ${className}`} data-testid="winter-banner">
      <div className="max-w-7xl mx-auto px-4 py-2 flex flex-wrap items-center justify-center gap-x-3 gap-y-1.5">
        <Snowflake size={16} className="text-sky-300 shrink-0 animate-pulse" />
        <span data-testid="winter-banner-text">{banner.text_i18n?.[lang] || banner.text}</span>
        {banner.has_fitment && vehicle && <span className="text-sky-300 text-xs hidden sm:inline" data-testid="winter-banner-vehicle">{vehicle.brand} {vehicle.model} · {banner.sizes.join(' / ')}</span>}
        <Link to={banner.link} data-testid="winter-banner-cta" className="inline-flex items-center gap-1 rounded-full bg-sky-300 text-sky-950 font-semibold px-3 py-1 text-xs hover:bg-white transition-colors">{cta} <ArrowRight size={12} /></Link>
        <button type="button" onClick={close} aria-label={t('winter.close')} data-testid="winter-banner-close" className="ml-1 p-1 rounded hover:bg-sky-900 text-sky-300"><X size={14} /></button>
      </div>
    </div>
  );
};

export default WinterTyresBanner;
